export type RegType = "Holding (4x)" | "Input (3x)" | "Coil (0x)" | "Discrete (1x)";
export type DataType = "INT16" | "INT16(U)" | "FLOAT32" | "FLOAT32(U)" | "INT32" | "INT32(U)" | "BOOL";
export type ParamType = "BU" | "BO" | "AI" | "AO" | "MI" | "MO";
export type RW = "R" | "R/W" | "W";
export type Verify = "ok" | "warn" | "bad";

export interface IEnumEntry {
  value: string;
  text: string;
}

export interface IRegisterRow {
  name: string;
  regType: RegType;
  dataType: DataType;
  scale: string;
  rw: RW;
  min: string;
  max: string;
  units: string;
  paramType: ParamType;
  enumMap?: IEnumEntry[];
  address: string;
  verify: Verify;
  reading: string;
  lastTest: string;
  universalEnabled?: boolean;
  universalPoint?: string;
}

export interface IAppUnitBlock {
  unitName: string;
  mapToUniversal: boolean;
  uiTemplate?: string;
  rows: IRegisterRow[];
}

export interface IDeviceConfig {
  deviceName: string;
  manufacturer: string;
  model: string;
  deviceType: string;
  connectionType: string;
  baudRate: string;
  dataBits: string;
  parity: string;
  stopBits: string;
  byteOrder: string;
  deviceId: string;
  timeout: string;
  notifyWrite: string;
}

export interface IMappedDevice {
  id: string;
  name: string;
  manufacturer: string;
  model: string;
  functionTemplate: string;
  appUnits: number;
  universal: boolean;
  status: "Connected" | "Draft" | "Disconnected";
  config: IDeviceConfig;
  appUnitBlocks: IAppUnitBlock[];
  bound?: boolean;
}

export interface IFunctionalUnitTemplateRow {
  name: string;
  regType: RegType;
  dataType: DataType;
  scale: string;
  rw: RW;
  min: string;
  max: string;
  units: string;
  paramType: ParamType;
  enumMap?: IEnumEntry[];
}

export interface IFunctionalUnitTemplate {
  name: string;
  category: string;
  typicalUse: string;
  status: "Published" | "Draft";
  uiTemplate: string;
  rows: IFunctionalUnitTemplateRow[];
}

export interface IDeviceTemplate {
  manufacturer: string;
  model: string;
  functionClass: string;
  registerProfile: string;
  mappedParameters: string;
  lastUpdate: string;
}

export interface IUiTemplateRow {
  name: string;
  paramType: ParamType;
  units: string;
  x: string;
  y: string;
}

export interface IUiTemplate {
  name: string;
  backgroundImage: string;
  lastUpdate: string;
  rows: IUiTemplateRow[];
}

export interface IFunctionTemplateLibraryEntry {
  name: string;
  functionClass: string;
  rows: IRegisterRow[];
}

export interface IDeviceLibraryEntry {
  id: string;
  name: string;
  manufacturer: string;
  model: string;
  deviceType: string;
  config: IDeviceConfig;
  registers: IRegisterRow[];
  mappings: { appUnit: string; units: string; type: ParamType; templateRegister: string; address: string }[];
}

export interface IInterfaceTemplateRow {
  name: string;
  registerType: number;
  dataType: number;
  scale: number;
  readOnly: boolean;
  min: number;
  max: number;
  engUnits: string;
  uiIdentifier: string;
  paramType?: ParamType;
}

export interface IInterfaceTemplate {
  id?: string;
  name: string;
  site?: string;
  category: number;
  defaultUiTemplate?: string;
  rows: IInterfaceTemplateRow[];
}

/* ------------------------------------------------------------------ *
 * Backend API model (matches /api/v2/modbus-*-templates contract).
 * `uiType` reuses the BACnet template "type" enum (see useTypes()).
 * `dataType` uses STRING register codes ("s16", …), not numeric ids.
 * ------------------------------------------------------------------ */

/** Register value encodings — string codes the backend expects. */
export const MODBUS_DATA_TYPE_CODES: Record<string, string> = {
  bool: "Boolean",
  u8: "Unsigned 8-bit Integer",
  s8: "Signed 8-bit Integer",
  u16: "Unsigned 16-bit Integer",
  s16: "Signed 16-bit Integer",
  u16be: "Unsigned 16-bit Big Endian",
  s16be: "Signed 16-bit Big Endian",
  f32: "32-bit Floating Point",
  f32be: "32-bit Floating Point Big Endian",
  u32: "Unsigned 32-bit Integer",
  s32: "Signed 32-bit Integer",
};
export type ModbusDataTypeCode = keyof typeof MODBUS_DATA_TYPE_CODES;

/** uiType numeric values (BACnet template-type enum). Source of truth: useTypes(). */
export const MODBUS_UI_TYPES: Record<number, string> = {
  1: "Binary",
  2: "Reverse Binary",
  4: "Enumeration",
  5: "Value",
};

export const UI_TYPE_BINARY = 1;
export const UI_TYPE_REVERSE_BINARY = 2;
export const UI_TYPE_ENUMERATION = 4;
export const UI_TYPE_VALUE = 5;

/**
 * Which universal entity a functional unit maps to. Matches
 * cr-backend-common's `protocolTypes.universalEntityPropertiesByEntityType`
 * keys — "controlUnit" has 9 AC-unit properties (operationStatus,
 * operationMode, fanMode, swingMode, setpoint, dualSetpointLow,
 * dualSetpointHigh, roomTemperature, errorCode); "powerMeter" has exactly
 * "power" and "energy".
 */
export type ModbusEntityType = "controlUnit" | "powerMeter";

export const MODBUS_ENTITY_TYPE_OPTIONS: { value: ModbusEntityType; label: string }[] = [
  { value: "controlUnit", label: "AC Unit" },
  { value: "powerMeter", label: "Power Meter" },
];

/** Value→label mapping for enumerated (uiType=4) parameters. */
export type ModbusCustomEnum = Record<string, string>;

export interface IModbusFunctionalRow {
  name: string;
  readOnly: boolean;
  uiType: number;
  units: string | null;
  multiplier?: number;
  minValue?: number | null;
  maxValue?: number | null;
  /** Increment for value-type (setpoint) parameters — e.g. 0.5°C. Defaults to 1 at runtime. */
  step?: number | null;
  registerType?: number;
  registerAddress?: number | null;
  dataType?: ModbusDataTypeCode | string;
  readFormula?: string | null;
  writeFormula?: string | null;
  /** Bit position (0-15) within the register that holds the discrete value. Only meaningful when `dataType` is "bool" and `registerType` isn't Coil. */
  bitNumber?: number | null;
  customEnum?: ModbusCustomEnum;
  /** Marks this register as an error point (mirrors BACnet's errorPointEnabled/errorPointValue). */
  errorPointEnabled?: boolean;
  /** Register value that clears the error, once errorPointEnabled is set. */
  errorPointValue?: number | null;
}

export interface IModbusFunctionalTemplate {
  id?: string;
  site?: string;
  customer?: string;
  syncId?: string;
  name: string;
  // An IUITemplate object (see UITemplateMapper) — typed loosely here to avoid
  // a circular import (UITemplateMapper imports from this file).
  defaultUiTemplate?: Record<string, any>;
  category?: number;
  rows: IModbusFunctionalRow[];
}

/** A reusable Control UI layout (/api/v2/modbus-ui-templates) — independent of any one functional template. */
export interface IModbusUiTemplate {
  id?: string;
  site?: string;
  customer?: string;
  syncId?: string;
  name: string;
  description?: string;
  deviceVendor?: string;
  deviceVendorId?: string;
  // Holds { backgroundImage?, points?, rows?, functionalUnitName? } — points reference rows by
  // index (see IUITemplate in UITemplateMapper), so rows travels with them for the entry to be
  // self-contained. Typed loosely here to avoid a circular import.
  data?: Record<string, any>;
}

export interface IModbusDeviceRow {
  name: string;
  readOnly: boolean;
  uiType: number;
  units: string | null;
  minValue: number | null;
  maxValue: number | null;
  /** Increment for value-type (setpoint) parameters — e.g. 0.5°C. Defaults to 1 at runtime. */
  step?: number | null;
  registerType: number;
  registerAddress: number | null;
  dataType: ModbusDataTypeCode | string;
  /** Bit position (0-15) within the register that holds the discrete value. Only meaningful when `dataType` is "bool" and `registerType` isn't Coil. */
  bitNumber?: number | null;
  multiplier: number;
  readFormula?: string | null;
  writeFormula?: string | null;
  customEnum?: ModbusCustomEnum;
  /** Universal control-unit mapping (mirrors BACnet). */
  property?: string;
  propertyMapping?: Record<string, string>;
  /** Marks this register as an error point (mirrors BACnet's errorPointEnabled/errorPointValue). */
  errorPointEnabled?: boolean;
  /** Register value that clears the error, once errorPointEnabled is set. */
  errorPointValue?: number | null;
}

export interface IModbusDeviceUnit {
  id?: string;
  name: string;
  functionalTemplate?: string;
  entityTemplate?: boolean;
  entityType?: ModbusEntityType;
  // An IUITemplate object (see UITemplateMapper) — typed loosely here to avoid
  // a circular import (UITemplateMapper imports from this file).
  uiTemplate?: Record<string, any>;
  rows: IModbusDeviceRow[];
}

export interface IModbusDeviceTemplate {
  id?: string;
  site?: string;
  device?: string;
  customer?: string;
  syncId?: string;
  name: string;
  vendorName: string;
  modelName: string;
  description?: string;
  timeoutMs?: number;
  units: IModbusDeviceUnit[];
}

export type ModbusLibraryTemplateType = "ui" | "functionalUnit" | "device";

export interface IModbusLibraryTemplate {
  id?: string;
  type: ModbusLibraryTemplateType;
  name: string;
  description?: string;
  manufacturer?: string;
  model?: string;
  fileName?: string;
  version?: number;
  createdAt?: string;
  updatedAt?: string;
  data?: Record<string, any>[];
}

export interface IModbusDevice {
  id?: string;
  site?: string;
  device?: string;
  syncId?: string;
  name: string;
  vendorName?: string;
  modelName?: string;
  interface?: string;
  line?: number;
  slaveId?: number;
  host?: string;
  port?: number;
  timeoutMs?: number;
  isConnected?: boolean;
  isEngaged?: boolean;
  deviceTemplate?: string;
  isUniversal?: boolean;
  unitsCount?: number;
  /** App unit id → name, for the App Units column's hover breakdown. */
  units?: Record<string, string>;
}

/** A Modbus unit under a device (/api/v2/modbus/units). */
export interface IModbusUnit {
  id?: string;
  name: string;
  title?: string;
  description?: string;
  isEngaged?: boolean;
  isVisible?: boolean;
  isConnected?: boolean;
  functionalTemplate?: string;
  uiTemplate?: string;
  paramTemplateData?: Record<string, any> | null;
  pollingIntervalMs?: number;
  modbusDevice?: string;
}

/** Shape of useTypes().universalEntityPropertiesByEntityType entries. */
export interface IUniversalEntityProperty {
  text: string;
  type: "enumerated" | "number" | "string";
  readOnly: boolean;
  enum?: Record<string, { text: string; value: number | string }>;
}
export type UniversalEntityPropertiesByEntityType = Record<
  ModbusEntityType,
  Record<string, IUniversalEntityProperty>
>;
